import React from 'react';
import classNames from 'classnames';
import { graphql, useStaticQuery } from 'gatsby';

import { Consulting } from '@layouts/Consulting';
import {
	Awards,
	DescriptionWithImage,
	InternalLinking,
	Reviews,
} from '@components';
import { Header, BusinessTransformationWithAI } from './components';
import ArrowRight from '@images/icons/arrow-right.svg';
import { internalLinkingData } from '@store/LocationAssetsTrackingData';
import SupportImage from '@static/images/figures/support.webp';
import useReviewsQuery from '@utils/hooks/useReviewsQuery';
import SoftwareSolutionImage from '@static/images/figures/software-solutions.webp';

import * as styles from './ArtificialIntelligence.module.scss';
import { expertiseData } from '@store/ArtificialIntelligenceData';

export const ArtificialIntelligence: React.FC = () => {
	const { directus } = useStaticQuery(graphql`
		query LocationAndAssetsReviews {
			directus {
				PageReviews(filter: { pageName: { _contains: "location-assets" } }) {
					Reviews {
						Reviews_id {
							quote
							reviewerName
							reviewerPosition
							link
							title
							logo {
								id
								imageFile {
									extension
									publicURL
									childImageSharp {
										gatsbyImageData
									}
								}
							}
						}
					}
				}
			}
		}
	`);
	const reviews = useReviewsQuery(directus);

	return (
		<div className="z-99 relative overflow-hidden">
			<Header />
			<DescriptionWithImage
				className="gray-bg"
				imageOnRight
				imageClassName={styles.image}
				image={SoftwareSolutionImage}
				disableContentPadding
			>
				<h2 className="font-size-medium-title mb-3">Comprehensive Approach</h2>
				<p className="font-size-sm mb-3">
					We specialize in developing AI-based software - from concept to
					implementation. We design and build systems for various industries,
					integrating cutting-edge technologies such as Machine Learning,
					Natural Language Processing (NLP), and Neural Networks. Whether it's
					data processing, IT systems automation, or creating smart devices, our
					solutions are customized to meet your company's specific needs.
				</p>
				<ul className={styles.list}>
					<p className="font-size-md mb-2 font-400">Our expertise includes:</p>

					{expertiseData.map((feature) => (
						<li
							key={feature.title}
							className={classNames(styles.listItem, 'mb-2')}
						>
							<div className="d-flex align-items-center">
								<img src={ArrowRight} alt="Arrow right" />
								<span className="font-600 font-size-sm">{feature.title}</span>
							</div>
							<p className="font-size-sm">{feature.description}</p>
						</li>
					))}
				</ul>
			</DescriptionWithImage>
			<BusinessTransformationWithAI />
			<DescriptionWithImage
				className="gray-bg"
				imageClassName={styles.image}
				image={SupportImage}
				imageOnRight
				disableContentPadding
			>
				<h2 className="font-size-md mb-2">
					Bridging Innovation with Practical Solutions
				</h2>
				<p className="mb-4 font-size-sm">
					Our AI solutions are applied across sectors such as automotive,
					construction, healthcare, finance, and retail. We specialize in
					integrating AI with IoT, creating intelligent devices and systems that
					enhance the efficient collaboration of machines and business
					processes.
				</p>
				<h2 className="font-size-md mb-2 mt-5">Technology at the Forefront</h2>
				<p className="mb-4 font-size-sm">
					We offer a comprehensive approach to AI solution development - from
					detailed documentation and project management to quality control.
					Utilizing the latest technologies and tools, we deliver reliable
					solutions that address current needs and support your company's future
					growth.
				</p>
			</DescriptionWithImage>
			<InternalLinking className="gray-bg" links={internalLinkingData} />
			{!!reviews.length && (
				<Reviews
					title="Our client says"
					reviews={reviews}
					maxWidth="1300px"
					bg="white"
					titleClass="font-size-medium"
				/>
			)}
			<Awards className="gray-bg" />
			<Consulting />
		</div>
	);
};
